/**
 * Structured date-format builder state <-> date-fns pattern.
 *
 * The canonical persisted value is the free-form `dateFormatPattern`
 * string; this module serializes builder state into a pattern and parses
 * a stored pattern back into state. Every pattern `buildPattern` can emit
 * stays within the backend allowlist (`date_format.py`), keeping the
 * date-fns preview and the babel-rendered report value in agreement.
 */

export type DateOrderOption =
  | "month_day_year"
  | "day_month_year"
  | "year_month_day"
  | "month_year"
  | "day_year"
  | "year_only";

export type MonthToken = "MMMM" | "MMM" | "MM" | "M";
export type DayToken = "dd" | "d";
export type YearToken = "yyyy" | "yy";
export type WeekdayToken = "EEEE" | "EEE";
export type DateSeparatorOption = "comma" | "slash" | "dash" | "space";

/** Read-side normalizer for the backend-persisted pattern: null, empty, and
 * whitespace-only values all fall back, so a degenerate stored value can't
 * blank the preview or round-trip back to the server as a "real" pattern. */
export const dateFormatPatternOrFallback = (
  pattern: string | null | undefined,
  fallback: string,
): string => {
  const trimmed = pattern?.trim();
  return trimmed ? trimmed : fallback;
};

export type DateFormatBuilderState = {
  order: DateOrderOption;
  monthToken: MonthToken;
  dayToken: DayToken;
  yearToken: YearToken;
  weekdayEnabled: boolean;
  weekdayToken: WeekdayToken;
  separator: DateSeparatorOption;
};

/** Parse of `DEFAULT_DATE_FORMAT_PATTERN` ("MMMM dd, yyyy") so an untouched
 * builder serializes back to the same default the mappers use. */
export const DEFAULT_BUILDER_STATE: DateFormatBuilderState = {
  order: "month_day_year",
  monthToken: "MMMM",
  dayToken: "dd",
  yearToken: "yyyy",
  weekdayEnabled: false,
  weekdayToken: "EEEE",
  separator: "comma",
};

export type DatePart = "month" | "day" | "year";

const ORDER_TO_PARTS: Record<DateOrderOption, ReadonlyArray<DatePart>> = {
  month_day_year: ["month", "day", "year"],
  day_month_year: ["day", "month", "year"],
  year_month_day: ["year", "month", "day"],
  month_year: ["month", "year"],
  day_year: ["day", "year"],
  year_only: ["year"],
};

/** Ordered date parts for an order option — drives the 1–3 "Date display"
 * inputs in the builder UI. */
export const getOrderParts = (order: DateOrderOption): ReadonlyArray<DatePart> =>
  ORDER_TO_PARTS[order];

/** Weekday only makes sense when the format includes a day part. */
export const supportsWeekday = (order: DateOrderOption): boolean =>
  ORDER_TO_PARTS[order].includes("day");

const partToken = (part: DatePart, state: DateFormatBuilderState): string => {
  switch (part) {
    case "month":
      return state.monthToken;
    case "day":
      return state.dayToken;
    case "year":
      return state.yearToken;
  }
};

// Comma is US-style date punctuation, not a uniform joiner: an adjacent
// pair joins with ", " when either side is the year, otherwise with a
// space. MDY -> "MMMM dd, yyyy", DMY -> "dd MMMM, yyyy",
// YMD -> "yyyy, MMMM dd".
const joiner = (separator: DateSeparatorOption, left: DatePart, right: DatePart): string => {
  switch (separator) {
    case "comma":
      return left === "year" || right === "year" ? ", " : " ";
    case "slash":
      return "/";
    case "dash":
      return "-";
    case "space":
      return " ";
  }
};

/** Serialize builder state into a date-fns / LDML pattern. */
export const buildPattern = (state: DateFormatBuilderState): string => {
  const parts = ORDER_TO_PARTS[state.order];
  let pattern = partToken(parts[0], state);
  for (let i = 1; i < parts.length; i += 1) {
    pattern += joiner(state.separator, parts[i - 1], parts[i]) + partToken(parts[i], state);
  }
  // Weekday always joins with ", " regardless of separator (per design).
  if (state.weekdayEnabled && supportsWeekday(state.order)) {
    pattern = `${state.weekdayToken}, ${pattern}`;
  }
  return pattern;
};

const MONTH_TOKENS: ReadonlySet<string> = new Set(["MMMM", "MMM", "MM", "M"]);
const DAY_TOKENS: ReadonlySet<string> = new Set(["dd", "d"]);
const YEAR_TOKENS: ReadonlySet<string> = new Set(["yyyy", "yy"]);

const PART_SEQUENCE_TO_ORDER: ReadonlyArray<{
  parts: ReadonlyArray<DatePart>;
  order: DateOrderOption;
}> = (Object.entries(ORDER_TO_PARTS) as Array<[DateOrderOption, ReadonlyArray<DatePart>]>).map(
  ([order, parts]) => ({ parts, order }),
);

const classifyToken = (token: string): DatePart | null => {
  if (MONTH_TOKENS.has(token)) return "month";
  if (DAY_TOKENS.has(token)) return "day";
  if (YEAR_TOKENS.has(token)) return "year";
  return null;
};

const SEPARATOR_OPTIONS: ReadonlyArray<DateSeparatorOption> = [
  "comma",
  "slash",
  "dash",
  "space",
];

const WEEKDAY_PREFIX_RE = /^(EEEE|EEE), /;
const LETTER_RUN_RE = /[A-Za-z]+/g;

/**
 * Parse a stored pattern back into builder state.
 *
 * Handles everything `buildPattern` can emit. Returns `null` for anything
 * else (including retired shapes like the old `QQQ yyyy` quarter format) —
 * callers seed `DEFAULT_BUILDER_STATE` and the stored pattern is
 * overwritten on the next save.
 */
export const parsePattern = (pattern: string): DateFormatBuilderState | null => {
  const trimmed = pattern.trim();
  if (!trimmed) return null;

  const weekdayMatch = WEEKDAY_PREFIX_RE.exec(trimmed);
  const body = weekdayMatch ? trimmed.slice(weekdayMatch[0].length) : trimmed;

  const tokens = body.match(LETTER_RUN_RE) ?? [];
  const parts: DatePart[] = [];
  for (const token of tokens) {
    const part = classifyToken(token);
    if (part === null) return null;
    parts.push(part);
  }

  const match = PART_SEQUENCE_TO_ORDER.find(
    ({ parts: candidate }) =>
      candidate.length === parts.length && candidate.every((p, i) => p === parts[i]),
  );
  if (!match) return null;

  const state: DateFormatBuilderState = {
    ...DEFAULT_BUILDER_STATE,
    order: match.order,
    weekdayEnabled: Boolean(weekdayMatch),
    weekdayToken: weekdayMatch
      ? (weekdayMatch[1] as WeekdayToken)
      : DEFAULT_BUILDER_STATE.weekdayToken,
  };
  for (const token of tokens) {
    if (MONTH_TOKENS.has(token)) state.monthToken = token as MonthToken;
    else if (DAY_TOKENS.has(token)) state.dayToken = token as DayToken;
    else if (YEAR_TOKENS.has(token)) state.yearToken = token as YearToken;
  }

  // Confirm separators by re-serializing: exactly one separator option can
  // reproduce the input verbatim (also rejects weekday on unsupported
  // orders, e.g. "EEEE, yyyy").
  for (const separator of SEPARATOR_OPTIONS) {
    const candidate: DateFormatBuilderState = { ...state, separator };
    if (buildPattern(candidate) === trimmed) return candidate;
  }
  return null;
};
